import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles } from "lucide-react";
import { OfferRideReview } from "./OfferRideReview";
import { RideStopLocationStep } from "./RideStopLocationStep";
import { RidePreferencesChargeStep } from "./RidePreferencesChargeStep";

interface StopLocation {
  id: string;
  name: string;
  coordinates: [number, number];
  type: 'pickup' | 'enroute' | 'dropoff';
}

interface RideData {
  from: string;
  to: string;
  pickup: string;
  drop: string;
  date: string;
  time: string;
  car: string;
  price: number;
  preferences: string[];
  seats: number;
  seatPrefs: string;
  baggage: string;
  note: string;
  stopLocations: StopLocation[];
  preferencesCharges?: { [key: string]: number };
}

type Step =
  | 1 // from/to
  | 2 // date/time
  | 3 // car
  | 4 // price
  | 5 // prefs
  | 6 // seats
  | 7 // baggage
  | 8 // note
  | 9 // stops
  | 10 // preferences charge
  | 11 // review
  ;

const stepLabels: Record<Step, string> = {
  1: "From / To",
  2: "Date / Time",
  3: "Car",
  4: "Price",
  5: "Preferences",
  6: "Seats",
  7: "Baggage",
  8: "Note",
  9: "Ride Stops",
  10: "Preference Charges",
  11: "Review",
};

export const OfferRideNewFlow: React.FC<{
  onBack: () => void;
  draftOffer?: RideData;
}> = ({ onBack, draftOffer }) => {
  const [rideData, setRideData] = useState<RideData>(draftOffer || {
    from: "", to: "", pickup: "", drop: "", date: "", time: "",
    car: "", price: 250, preferences: [], seats: 1, seatPrefs: "",
    baggage: "", note: "", stopLocations: [], preferencesCharges: {}
  });
  const [step, setStep] = useState<Step>(1);
  const [messages, setMessages] = useState<{ step: Step; role: "system" | "you"; content: string }[]>([
    { step: 1, role: "system", content: "Hi! I'm your Chalo AI assistant. I'll help you create the perfect ride offer step by step. Let's start with your route - where would you like to go?" }
  ]);

  const handleEdit = (editStep: Step) => {
    setStep(editStep);
    setMessages(msgs => msgs.filter(m => m.step <= editStep));
  };

  const setMessageAndNext = (yourContent: string, dataUpdate: Partial<RideData> = {}, systemResponse?: string, goToStep?: Step) => {
    setRideData(rd => ({ ...rd, ...dataUpdate }));
    const newStep = goToStep || (step < 11 ? ((step + 1) as Step) : step);
    
    setMessages(msgs => [...msgs, { step, role: "you", content: yourContent }]);
    
    if (systemResponse && newStep !== 11) {
      setTimeout(() => {
        setMessages(msgs => [...msgs, { step: newStep, role: "system", content: systemResponse }]);
      }, 500);
    }
    
    setTimeout(() => {
      setStep(newStep);
    }, 100);
  };

  const getSystemMessage = (stepNum: Step): string => {
    switch (stepNum) {
      case 2:
        return "Perfect route selection! When are you planning to travel? Setting the right time helps match you with travelers going the same way.";
      case 3:
        return "Excellent timing! Now, which car will you be driving? I'll help travelers know what to expect.";
      case 4:
        return `Nice choice! Based on similar routes, I recommend ₹250 as the optimal price. You can adjust it to maximize bookings or earnings.`;
      case 5:
        return "Smart pricing! Now let's set your ride preferences. These help create a comfortable experience for everyone.";
      case 6:
        return "Great preferences! How many passengers can you accommodate? More seats mean more earnings potential.";
      case 7:
        return "Perfect! Let's talk about baggage. Setting clear expectations helps avoid any confusion.";
      case 8:
        return "Almost done! Add a personal note to make travelers feel welcome. This can really help with bookings!";
      case 9:
        return "Great! Now let's set up your ride stops - pickup points, en-route stops, and drop-off locations. This helps travelers find convenient meeting points.";
      case 10:
        return "Excellent! You can optionally set charges for special preferences. This is a great way to earn Chalo Good Will points and show your generosity!";
      case 11:
        return "Fantastic! You've created an amazing ride offer. Let me show you the final review before we publish it.";
      default:
        return "";
    }
  };

  if (step === 9) {
    return (
      <RideStopLocationStep
        from={rideData.from}
        to={rideData.to}
        selectedStops={rideData.stopLocations}
        onStopsSelected={(stops) => setRideData(rd => ({ ...rd, stopLocations: stops }))}
        onBack={() => setStep(8)}
        onComplete={() => {
          setMessageAndNext(
            `Stop locations selected: ${rideData.stopLocations.length} stops`,
            {},
            getSystemMessage(10),
            10
          );
        }}
      />
    );
  }

  if (step === 10) {
    return (
      <RidePreferencesChargeStep
        preferences={rideData.preferences}
        currentCharges={rideData.preferencesCharges || {}}
        onChargesSet={(charges) => setRideData(rd => ({ ...rd, preferencesCharges: charges }))}
        onBack={() => setStep(9)}
        onComplete={() => {
          setMessageAndNext(
            `Preference charges set: ${Object.keys(rideData.preferencesCharges || {}).length} preferences`,
            {},
            getSystemMessage(11),
            11
          );
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[101] bg-gradient-to-br from-orange-50 via-white to-blue-50 flex flex-col">
      <div className="flex items-center p-4 border-b bg-white">
        <Button variant="ghost" onClick={onBack} size="sm" className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 flex items-center justify-center gap-2">
          <Sparkles className="w-5 h-5 text-[#22C55E]" />
          <h2 className="font-semibold text-gray-800 text-lg">Chalo AI Assistant</h2>
        </div>
        <div className="w-10"></div>
      </div>
      
      <div className="flex-1 overflow-y-auto px-3 py-4">
        {/* Chat Messages */}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`mb-3 flex ${m.role === "you" ? "justify-end" : "justify-start"}`}
          >
            <div className={`max-w-xs ${m.role === "you" ? "" : "flex items-start gap-2"}`}>
              {m.role === "system" && (
                <div className="w-8 h-8 bg-[#22C55E] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
              )}
              <div
                className={`rounded-lg px-4 py-2 shadow text-sm ${
                  m.role === "you" 
                    ? "bg-[#22C55E] text-white cursor-pointer hover:ring-2 hover:ring-[#22C55E]/30" 
                    : "bg-white text-gray-800 border"
                }`}
                onClick={() => m.role === "you" && handleEdit(m.step)}
              >
                {m.content}
                {m.role === "you" && (
                  <span className="ml-2 text-xs text-orange-200 underline">Edit</span>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Current Step Input */}
        {step === 1 && (
          <div className="mt-4">
            <div className="grid grid-cols-2 gap-2">
              <input
                className="px-3 py-2 border rounded"
                placeholder="From"
                value={rideData.from}
                onChange={e => setRideData(rd => ({ ...rd, from: e.target.value }))}
              />
              <input
                className="px-3 py-2 border rounded"
                placeholder="To"
                value={rideData.to}
                onChange={e => setRideData(rd => ({ ...rd, to: e.target.value }))}
              />
            </div>
            <Button className="w-full mt-3"
              disabled={!rideData.from || !rideData.to}
              onClick={() =>
                setMessageAndNext(
                  `From: ${rideData.from}, To: ${rideData.to}`, 
                  {}, 
                  getSystemMessage(2),
                  2
                )
              }
            >Continue</Button>
          </div>
        )}

        {step === 2 && (
          <div className="mt-4">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                className="px-3 py-2 border rounded"
                placeholder="Date"
                value={rideData.date}
                onChange={e => setRideData(rd => ({ ...rd, date: e.target.value }))}
              />
              <input
                type="time"
                className="px-3 py-2 border rounded"
                placeholder="Time"
                value={rideData.time}
                onChange={e => setRideData(rd => ({ ...rd, time: e.target.value }))}
              />
            </div>
            <Button className="w-full mt-3"
              onClick={() =>
                setMessageAndNext(
                  `Date: ${rideData.date}, Time: ${rideData.time}`,
                  {},
                  getSystemMessage(3),
                  3
                )
              }
            >Continue</Button>
          </div>
        )}

        {step === 3 && (
          <div className="mt-4 space-y-2">
            <Button
              className="w-full"
              onClick={() => setMessageAndNext(
                "Honda City - MH12AB1234", 
                { car: "Honda City - MH12AB1234" },
                getSystemMessage(4),
                4
              )}
            >Honda City - MH12AB1234</Button>
            <Button
              className="w-full"
              onClick={() => setMessageAndNext(
                "Maruti Swift - MH12CD5678", 
                { car: "Maruti Swift - MH12CD5678" },
                getSystemMessage(4),
                4
              )}
              variant="outline"
            >Maruti Swift - MH12CD5678</Button>
          </div>
        )}

        {step === 4 && (
          <div className="mt-4">
            <div className="text-center mb-2">
              <span className="text-sm text-gray-600">Recommended: </span>
              <span className="text-[#22C55E] font-bold">₹250</span>
            </div>
            <input
              type="range"
              min={100}
              max={350}
              step={10}
              value={rideData.price}
              onChange={e => setRideData(rd => ({ ...rd, price: +e.target.value }))}
              className="w-full"
            />
            <div className="flex justify-between text-xs mt-1">
              <span>₹100</span>
              <span className="font-bold">₹{rideData.price}</span>
              <span>₹350</span>
            </div>
            <Button className="w-full mt-3"
              onClick={() =>
                setMessageAndNext(
                  `Price set: ₹${rideData.price}`,
                  {},
                  getSystemMessage(5),
                  5
                )
              }
            >Continue</Button>
          </div>
        )}

        {step === 5 && (
          <div className="mt-4">
            <div className="flex flex-wrap gap-2 mb-3">
            {["Music","AC","No Smoking","Snacks"].map(pref => (
              <Button key={pref}
                variant={rideData.preferences.includes(pref) ? "default" : "outline"}
                className="rounded-full"
                size="sm"
                onClick={() =>
                  setRideData(rd => ({
                    ...rd,
                    preferences: rd.preferences.includes(pref)
                      ? rd.preferences.filter(p => p !== pref)
                      : [...rd.preferences, pref]
                  }))
                }
              >{pref}</Button>
            ))}
            </div>
            <Button className="w-full"
              onClick={() =>
                setMessageAndNext(
                  `Preferences: ${rideData.preferences.length > 0 ? rideData.preferences.join(", ") : "None selected"}`,
                  {},
                  getSystemMessage(6),
                  6
                )
              }
            >Continue</Button>
          </div>
        )}

        {step === 6 && (
          <div className="mt-4">
            <input type="number" min={1} max={6}
              className="px-3 py-2 border rounded w-full"
              value={rideData.seats}
              onChange={e => setRideData(rd => ({ ...rd, seats: +e.target.value }))}
            />
            <input
              className="px-3 py-2 border rounded w-full mt-2"
              placeholder="Seat preferences (optional)"
              value={rideData.seatPrefs}
              onChange={e => setRideData(rd => ({ ...rd, seatPrefs: e.target.value }))}
            />
            <Button className="w-full mt-3"
              onClick={() =>
                setMessageAndNext(
                  `Seats: ${rideData.seats}${rideData.seatPrefs ? `, Preferences: ${rideData.seatPrefs}` : ""}`,
                  {},
                  getSystemMessage(7),
                  7
                )
              }
            >Continue</Button>
          </div>
        )}

        {step === 7 && (
          <div className="mt-4">
            <input
              className="px-3 py-2 border rounded w-full"
              placeholder="Baggage preference"
              value={rideData.baggage}
              onChange={e => setRideData(rd => ({ ...rd, baggage: e.target.value }))}
            />
            <Button className="w-full mt-3"
              onClick={() =>
                setMessageAndNext(
                  `Baggage: ${rideData.baggage || "No specific preference"}`,
                  {},
                  getSystemMessage(8),
                  8
                )
              }
            >Continue</Button>
          </div>
        )}

        {step === 8 && (
          <div className="mt-4">
            <textarea
              className="px-3 py-2 border rounded w-full"
              placeholder="Personal note (optional)"
              value={rideData.note}
              rows={3}
              onChange={e => setRideData(rd => ({ ...rd, note: e.target.value }))}
            />
            <Button className="w-full mt-3"
              onClick={() =>
                setMessageAndNext(
                  `Note: ${rideData.note || "No additional notes"}`,
                  {},
                  getSystemMessage(9),
                  9
                )
              }
            >Continue</Button>
          </div>
        )}

        {step === 11 && (
          <OfferRideReview
            rideData={rideData}
            onEdit={editKey => {
              const editStepMap: Record<string, Step> = {
                'route': 1,
                'datetime': 2,
                'car': 3,
                'price': 4,
                'prefs': 5,
                'seats': 6,
                'baggage': 7,
                'note': 8,
                'stops': 9,
                'preferences-charge': 10
              };
              const targetStep = editStepMap[editKey] || 1;
              handleEdit(targetStep);
            }}
            onBack={onBack}
          />
        )}
      </div>
    </div>
  );
};
